import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Input, Radio, Button, Spin } from 'antd';
import AccentButton from '../components/AccentButton/AccentButton';
import { UserTag } from '../components/UserTag/UserTag';
import type { UserType } from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import { DefaultAvatar, CogIcon, LockClosedIcon } from '../components/icons';
import SpecModal from '../components/SpecModal/SpecModal';
import SpecBottomSheet from '../components/SpecBottomSheet/SpecBottomSheet';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import EditingBar from '../components/EditingBar/EditingBar';
import { useAuth } from '../contexts/AuthContext';
import { useFanProfile } from '../api/queries';
import { update_my_profile, upload_my_photo, upgrade_to_musician, remove_my_show_contributor } from '../api';
import './EditProfile.css';
import './UserProfile.css';

const EditFanProfile: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user, refreshAuth } = useAuth();
  // Fans have no Firestore profile doc - get_fan_profile aggregates Firebase
  // Auth + comments/media/contributorBands live. Works for any uid, not just
  // "real" fans, so this always resolves for a signed-in user.
  const { data: fanProfile, isLoading: fanProfileLoading } = useFanProfile(user?.uid);

  const [name, setName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  // Seed the form once, without clobbering in-progress edits if auth context
  // happens to update in the background.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (user && !initializedRef.current) {
      initializedRef.current = true;
      setName(user.displayName || '');
      setAvatarUrl(user.photoURL || undefined);
    }
  }, [user]);

  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [removeContributorModalOpen, setRemoveContributorModalOpen] = useState(false);
  const [removeBandId, setRemoveBandId] = useState<string | null>(null);
  const [removeBandName, setRemoveBandName] = useState('');
  const [deletePasscode, setDeletePasscode] = useState('');
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [removingContributor, setRemovingContributor] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      await update_my_profile({
        displayName: name,
        // Explicit '' (not omitted) so a removed photo actually clears
        // photoURL server-side instead of leaving the old value untouched.
        photoURL: avatarUrl || '',
      });
      // displayName/photoURL aren't part of the JWT claims, but AuthContext's
      // user state only updates via signIn/signUp/refreshAuth - without this,
      // pages reading useAuth().user directly (e.g. UserProfile.tsx) would
      // keep showing the stale name/photo until the next full session refresh.
      await refreshAuth();
      navigate('/fan-profile', { state: { profileSaved: true } });
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to save profile');
      setSaving(false);
    }
  }, [user, saving, name, avatarUrl, refreshAuth, navigate]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setPhotoSheetOpen(false);
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const result = await upload_my_photo(file);
      setAvatarUrl(result.url);
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setAvatarUrl(undefined);
    setPhotoSheetOpen(false);
  };

  const handleConfirmUpgrade = async () => {
    setUpgrading(true);
    try {
      await upgrade_to_musician({});
      await refreshAuth();
      queryClient.invalidateQueries({ queryKey: ['my-musician'] });
      setUpgradeModalOpen(false);
      navigate('/musician-profile', { state: { profileSaved: true } });
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to switch to a musician account');
      setUpgrading(false);
    }
  };

  const handleConfirmRemoveContributor = async () => {
    if (!user || !removeBandId) return;
    setRemovingContributor(true);
    try {
      await remove_my_show_contributor(removeBandId);
      await queryClient.invalidateQueries({ queryKey: ['fan', user.uid] });
      setRemoveContributorModalOpen(false);
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to remove as show contributor');
    } finally {
      setRemovingContributor(false);
    }
  };

  if (!user) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Welcome to Spec</h2>
          <p className="user-profile__empty-subtitle">Sign in to edit your profile.</p>
        </div>
      </div>
    );
  }

  if (fanProfileLoading) {
    return (
      <div className="edit-profile-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const contributorBands = fanProfile?.contributorBands || [];
  const isShowContributor = contributorBands.length > 0;

  return (
    <div className="edit-profile-page">
      <EditingBar
        icon={<CogIcon size="m" tone="white" />}
        title="Editing Profile"
        onCancel={() => navigate(-1)}
        onSave={handleSave}
      />
      <div className="edit-profile-content">
        {/* Avatar Section */}
        <div className="edit-profile-avatar-section edit-profile-avatar-section--row">
          <div
            className="edit-profile-avatar edit-profile-avatar--circle edit-profile-avatar--lg"
            onClick={() => setPhotoSheetOpen(true)}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt={name} />
            ) : (
              <DefaultAvatar size={48} />
            )}
          </div>
          <div className="edit-profile-avatar-info">
            <div className="edit-profile-avatar-name">{name}</div>
            <div className="edit-profile-avatar-badges">
              <UserTag type="fan" onClick={() => setOpenInfoType('fan')} />
              {isShowContributor && (
                <UserTag type="showContributor" onClick={() => setOpenInfoType('showContributor')} />
              )}
            </div>
          </div>
        </div>
        <Button type="primary" size="small" shape="round" className="edit-profile-update-photo" loading={uploadingPhoto} disabled={uploadingPhoto} onClick={() => setPhotoSheetOpen(true)}>
          Update Photo
        </Button>

        {/* Form */}
        <div className="edit-profile-form">

          {/* Name */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Name</label>
            <Input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Bio/Website/Instagram fields not included - fans have no
              Firestore profile doc, so there's nowhere to persist them today. */}

          {/* Location */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Location</label>
            <Input
              prefix="📍"
              placeholder="City, State"
              value="Tulsa, OK."
              disabled
              suffix={<LockClosedIcon size="s" tone="lightGrey" />}
              className="edit-profile-location-input"
            />
            <span className="edit-profile-location-note">
              *our launch is currently limited to Tulsa
            </span>
          </div>

          {/* Account Type */}
          <div className="edit-profile-field">
            <h2 className="edit-profile-label">Account Type</h2>
            <div className="edit-profile-account-types">
              <button className="edit-profile-account-type edit-profile-account-type--selected">
                <div className="edit-profile-account-type-info">
                  <span className="edit-profile-account-type-name">Fan Account</span>
                  <div className="edit-profile-account-type-perks">
                    <span className="edit-profile-account-type-perk">• Comment on shows</span>
                    <span className="edit-profile-account-type-perk">• Add photos to shows</span>
                  </div>
                </div>
                <Radio checked className="edit-profile-account-type-radio" />
              </button>

              <button
                className="edit-profile-account-type"
                onClick={() => setUpgradeModalOpen(true)}
              >
                <div className="edit-profile-account-type-info">
                  <span className="edit-profile-account-type-name">Musician Account</span>
                  <div className="edit-profile-account-type-perks">
                    <span className="edit-profile-account-type-perk">• Comment on shows</span>
                    <span className="edit-profile-account-type-perk">• Add photos to shows</span>
                    <span className="edit-profile-account-type-perk">• Directory listing and bio</span>
                    <span className="edit-profile-account-type-perk">• Add bands</span>
                    <span className="edit-profile-account-type-perk">• Add shows</span>
                  </div>
                </div>
                <Radio checked={false} className="edit-profile-account-type-radio" />
              </button>
            </div>
          </div>

          {/* Comments and Photos info */}
          <div className="edit-profile-field">
            <h2 className="edit-profile-label">Comments and Photos</h2>
            <p className="edit-profile-info-block">
            Comments and photos you share on a show can be seen on the show, on the musicians profile, on the bands profile, and on the venue page 🤘
            </p>
          </div>

          {/* Show Contributor */}
          {isShowContributor && (
            <div className="edit-profile-field">
              <h2 className="edit-profile-label">Show Contributor</h2>
              <p className="edit-profile-info-block">
                Bands you can add shows for as a show contributor
              </p>
              <div className="edit-profile-band-list">
                {contributorBands.map((band) => (
                  <div key={band.id} className="edit-profile-band-entry">
                    <div className="edit-profile-band-item">
                      <div className="edit-profile-band-avatar">
                        <DefaultAvatar size={20} />
                      </div>
                      <span className="edit-profile-band-name">{band.name}</span>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      shape="round"
                      onClick={() => { setRemoveBandId(band.id); setRemoveBandName(band.name); setRemoveContributorModalOpen(true); }}
                    >
                      Remove me
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delete Account */}
          <button
            className="edit-profile-danger-link"
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Photo Action Sheet */}
      <SpecBottomSheet open={photoSheetOpen} onClose={() => setPhotoSheetOpen(false)}>
        <div className="edit-profile-photo-actions">
          <button
            className="edit-profile-photo-action edit-profile-photo-action--danger"
            onClick={handleRemovePhoto}
          >
            Remove Photo
          </button>
          <button
            className="edit-profile-photo-action edit-profile-photo-action--primary"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload from Photos
          </button>
        </div>
      </SpecBottomSheet>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="edit-profile-file-input"
        onChange={handlePhotoUpload}
      />

      {/* Upgrade to Musician Modal */}
      <SpecModal
        open={upgradeModalOpen}
        onCancel={() => setUpgradeModalOpen(false)}
        title="Switch to a Musician account"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          This creates a new musician profile linked to your account. Your fan activity (comments, photos) stays intact.
        </p>
        <div className="edit-profile-modal-actions">
          <Button
            size="large"
            shape="round"
            onClick={() => setUpgradeModalOpen(false)}
          >
            Cancel
          </Button>
          <AccentButton
            size="large"
            shape="round"
            loading={upgrading}
            onClick={handleConfirmUpgrade}
          >
            Switch to Musician
          </AccentButton>
        </div>
      </SpecModal>

      {/* Remove Contributor Modal */}
      <SpecModal
        open={removeContributorModalOpen}
        onCancel={() => setRemoveContributorModalOpen(false)}
        title="Remove me as Show Contributor"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Are you sure you want to remove yourself as a show contributor for {removeBandName}?
        </p>
        <div className="addshow-modal-actions">
          <Button
            size="large"
            shape="round"
            onClick={() => setRemoveContributorModalOpen(false)}
          >
            Cancel
          </Button>
          <AccentButton
            size="large"
            shape="round"
            loading={removingContributor}
            onClick={handleConfirmRemoveContributor}
          >
            Remove me
          </AccentButton>
        </div>
      </SpecModal>

      {/* Delete Account Modal */}
      <SpecModal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        title="Delete Account"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Type the "deletemyaccount" to permanently delete account.
        </p>
        <div className="edit-profile-delete-form">
          <span className="edit-profile-delete-label">Delete Passcode:</span>
          <Input
            placeholder="Enter passcode to delete"
            value={deletePasscode}
            onChange={(e) => setDeletePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={deletePasscode !== 'deletemyaccount'}
            onClick={() => {
              console.log('Account deleted');
              setDeleteModalOpen(false);
            }}
          >
            DELETE ACCOUNT
          </Button>
        </div>
      </SpecModal>

      <UserTypeInfo type="fan" open={openInfoType === 'fan'} onClose={() => setOpenInfoType(null)} />
      <UserTypeInfo type="showContributor" open={openInfoType === 'showContributor'} onClose={() => setOpenInfoType(null)} />

      <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />
    </div>
  );
};

export default EditFanProfile;
